import { Controller } from "@hotwired/stimulus"

// V2 hierarchical account selector with parent→child cascade.
// The form's correctness comes from the visible checkboxes themselves —
// each carries its own `name` attribute, so the form posts the right values
// even if this controller never connects. Everything here is UI enhancement:
// cascade, search, chips, and a change event for outer controllers to react to.
export default class extends Controller {
  static targets = [
    "root", "search", "list", "chips", "emptyState",
    "row", "childRow",
    "expandToggle", "children",
    "parentCheckbox", "childCheckbox",
    "childCount"
  ]
  static values = {
    selectedIds: Array,
    disabled: { type: Boolean, default: false }
  }

  connect() {
    this.selected = new Set(this.selectedIdsValue.map(Number))
    this.initialSelected = new Set(this.selected)
    this.syncCheckboxesFromState()
    this.syncChips()
    this.updateAllParentStates()
    this.updateAllChildCounts()
    if (this.disabledValue) this.setDisabled(true)
  }

  // ─── Selection ─────────────────────────────────────────────────────────

  parentToggled(event) {
    const cb = event.target
    const parentId = Number(cb.dataset.accountTreeParentId)
    const shouldCheck = cb.checked

    this.toggleId(parentId, shouldCheck)
    this.childCheckboxesFor(parentId).forEach((childCb) => {
      childCb.checked = shouldCheck
      this.toggleId(Number(childCb.value), shouldCheck)
    })

    cb.indeterminate = false
    this.updateChildCount(parentId)
    this.afterSelectionChange()
  }

  childToggled(event) {
    const cb = event.target
    const parentId = Number(cb.dataset.accountTreeParentId)
    this.toggleId(Number(cb.value), cb.checked)
    this.updateParentState(parentId)
    this.updateChildCount(parentId)
    this.afterSelectionChange()
  }

  removeChip(event) {
    const id = Number(event.currentTarget.dataset.accountId)
    this.toggleId(id, false)
    this.syncCheckboxesFromState()
    this.updateAllParentStates()
    this.updateAllChildCounts()
    this.afterSelectionChange()
  }

  toggleId(id, on) {
    if (on) this.selected.add(id)
    else this.selected.delete(id)
  }

  // ─── Expand / collapse ─────────────────────────────────────────────────

  toggleExpand(event) {
    event.preventDefault()
    const parentId = Number(event.currentTarget.dataset.accountTreeParentId)
    const children = this.childrenContainerFor(parentId)
    const icon = event.currentTarget.querySelector("svg")
    if (!children) return
    const open = !children.classList.contains("hidden")
    children.classList.toggle("hidden", open)
    if (icon) icon.style.transform = open ? "" : "rotate(90deg)"
  }

  // ─── Search ────────────────────────────────────────────────────────────

  filter() {
    const term = this.searchTarget.value.trim().toLowerCase()
    let anyVisible = false

    this.rowTargets.forEach((row) => {
      const parentMatches = row.dataset.accountTreeRowName.includes(term)
      let childMatches = false

      const parentId = Number(row.dataset.accountTreeRowId)
      const childRows = this.childRowTargets.filter((cr) =>
        Number(cr.dataset.accountTreeRowParentId) === parentId
      )

      childRows.forEach((cr) => {
        const match = cr.dataset.accountTreeRowName.includes(term)
        cr.classList.toggle("hidden", term !== "" && !match)
        if (match) childMatches = true
      })

      const show = term === "" || parentMatches || childMatches
      row.classList.toggle("hidden", !show)
      if (show) anyVisible = true

      if (term !== "" && childMatches) {
        const container = this.childrenContainerFor(parentId)
        if (container) container.classList.remove("hidden")
      }
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", anyVisible)
    }
  }

  // ─── External control ─────────────────────────────────────────────────

  setDisabled(disabled) {
    this.disabledValue = disabled
    this.parentCheckboxTargets.forEach((cb) => { cb.disabled = disabled })
    this.childCheckboxTargets.forEach((cb) => { cb.disabled = disabled })
    if (this.hasSearchTarget) this.searchTarget.disabled = disabled
  }

  restrictToRelated(accountIds) {
    const ids = new Set(accountIds.map(Number))
    if (ids.size === 0) {
      this.parentCheckboxTargets.forEach((cb) => { cb.disabled = false })
      this.childCheckboxTargets.forEach((cb) => { cb.disabled = false })
      return
    }

    const allowedParentIds = new Set()
    this.rowTargets.forEach((row) => {
      const parentId = Number(row.dataset.accountTreeRowId)
      if (ids.has(parentId)) {
        allowedParentIds.add(parentId)
        return
      }
      const childRows = this.childRowTargets.filter((cr) =>
        Number(cr.dataset.accountTreeRowParentId) === parentId
      )
      if (childRows.some((cr) => ids.has(Number(cr.dataset.accountTreeRowId)))) {
        allowedParentIds.add(parentId)
      }
    })

    this.parentCheckboxTargets.forEach((cb) => {
      const parentId = Number(cb.dataset.accountTreeParentId)
      cb.disabled = !allowedParentIds.has(parentId)
    })
    this.childCheckboxTargets.forEach((cb) => {
      const parentId = Number(cb.dataset.accountTreeParentId)
      cb.disabled = !allowedParentIds.has(parentId)
    })
  }

  clearRestrictions() {
    this.parentCheckboxTargets.forEach((cb) => { cb.disabled = false })
    this.childCheckboxTargets.forEach((cb) => { cb.disabled = false })
  }

  // ─── DOM sync ──────────────────────────────────────────────────────────

  syncCheckboxesFromState() {
    this.parentCheckboxTargets.forEach((cb) => {
      cb.checked = this.selected.has(Number(cb.value))
    })
    this.childCheckboxTargets.forEach((cb) => {
      cb.checked = this.selected.has(Number(cb.value))
    })
  }

  syncChips() {
    const container = this.chipsTarget
    container.innerHTML = ""
    if (this.selected.size === 0) {
      const empty = document.createElement("span")
      empty.className = "text-xs text-sg-gray-400"
      empty.textContent = "No accounts selected"
      container.appendChild(empty)
      return
    }
    this.selected.forEach((id) => {
      const name = this.accountNameFor(id)
      if (!name) return
      const chip = document.createElement("button")
      chip.type = "button"
      chip.className = "inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sg-gray-100 text-xs text-sg-gray-700 hover:bg-sg-gray-200"
      chip.dataset.accountId = String(id)
      chip.dataset.action = "click->account-tree#removeChip"
      chip.textContent = name
      const x = document.createElement("span")
      x.className = "text-sg-gray-400"
      x.textContent = "×"
      chip.appendChild(x)
      container.appendChild(chip)
    })
  }

  updateParentState(parentId) {
    const cb = this.parentCheckboxFor(parentId)
    if (!cb) return
    const children = this.childCheckboxesFor(parentId)
    if (children.length === 0) return
    const checkedCount = children.filter((c) => c.checked).length
    if (checkedCount === 0) {
      cb.checked = false
      cb.indeterminate = false
    } else if (checkedCount === children.length) {
      cb.checked = true
      cb.indeterminate = false
    } else {
      cb.checked = false
      cb.indeterminate = true
    }
  }

  updateAllParentStates() {
    this.parentCheckboxTargets.forEach((cb) => {
      this.updateParentState(Number(cb.dataset.accountTreeParentId))
    })
  }

  updateChildCount(parentId) {
    const counter = this.childCountTargets.find((el) =>
      Number(el.dataset.accountTreeParentId) === parentId
    )
    if (!counter) return
    const checked = this.childCheckboxesFor(parentId).filter((c) => c.checked).length
    counter.textContent = String(checked)
  }

  updateAllChildCounts() {
    this.parentCheckboxTargets.forEach((cb) => {
      this.updateChildCount(Number(cb.dataset.accountTreeParentId))
    })
  }

  afterSelectionChange() {
    this.syncChips()
    this.dispatchChangeEvent()
  }

  dispatchChangeEvent() {
    const selectedArr = Array.from(this.selected)
    const initial = this.initialSelected
    const added = selectedArr.filter((id) => !initial.has(id))
    const removed = Array.from(initial).filter((id) => !this.selected.has(id))
    this.element.dispatchEvent(new CustomEvent("account-tree:change", {
      bubbles: true,
      detail: { selectedIds: selectedArr, added, removed }
    }))
  }

  // ─── Lookups ───────────────────────────────────────────────────────────

  parentCheckboxFor(parentId) {
    return this.parentCheckboxTargets.find((cb) =>
      Number(cb.dataset.accountTreeParentId) === parentId
    )
  }

  childCheckboxesFor(parentId) {
    return this.childCheckboxTargets.filter((cb) =>
      Number(cb.dataset.accountTreeParentId) === parentId
    )
  }

  childrenContainerFor(parentId) {
    return this.childrenTargets.find((el) =>
      Number(el.dataset.accountTreeParentId) === parentId
    )
  }

  accountNameFor(id) {
    const parent = this.rowTargets.find((row) => Number(row.dataset.accountTreeRowId) === id)
    if (parent) return parent.querySelector("label span")?.textContent
    const child = this.childRowTargets.find((row) => Number(row.dataset.accountTreeRowId) === id)
    return child?.querySelector("label span")?.textContent
  }
}
